import React, { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
  Button, 
  Breadcrumb, 
  Search, 
  Icon, 
  Dropdown, 
  ButtonIcon,
  TextInput
} from "pepsico-ds";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoading } from "../../loader";
import "./sso_onboarding.css";
import "./sso_onboarding_details.css";

function useDebouncedCallback(callback, delay = 500) {
  const timerRef = useRef();
  const callbackRef = useRef(callback);

  // Update the callback ref on every render to always have the latest version
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debounced = useCallback((...args) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      callbackRef.current(...args);
    }, delay);
  }, [delay]);

  return debounced;
}

const ASSOCIATE_SSO_FAQ_URL = "https://pepsico.sharepoint.com/teams/SharedContent/SitePages/Associate-SSO-Integration-FAQ.aspx?ga=1";

const SSOOnboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { REACT_APP_API_ENDPOINT } = process.env;
  const { showLoader, hideLoader } = useLoading();
  const detailsRoute = "/sso-onboarding-details";
  const pageTitle = t('sso_onboarding.SSO Onboarding');

  // Get data from navigation state (when coming back from details page)
  const editingData = location.state;

  // Form state - Dropdowns
  const [selectedSector, setSelectedSector] = useState(
    editingData?.businessInfo?.sector ? [{ id: editingData.businessInfo.sector, displayText: editingData.businessInfo.sector }] : null
  );
  const [selectedEnvironment, setSelectedEnvironment] = useState(
    editingData?.businessInfo?.environment ? [{ id: editingData.businessInfo.environment, displayText: editingData.businessInfo.environment }] : null
  );
  const [selectedDataClassification, setSelectedDataClassification] = useState(null);
  const [selectedIntegrationType, setSelectedIntegrationType] = useState(editingData?.selectedType || null);
  
  // Search states - Business Application
  const [businessAppSuggestions, setBusinessAppSuggestions] = useState([]);
  const [businessAppSearchText, setBusinessAppSearchText] = useState('');
  const [selectedBusinessApp, setSelectedBusinessApp] = useState(
    editingData?.businessInfo?.businessApplication 
      ? { id: editingData.businessInfo.businessApplication, label: editingData.businessInfo.businessApplication }
      : null
  );

  // Application Service options (populated from Business Application API)
  const [applicationServiceOptions, setApplicationServiceOptions] = useState(
    editingData?.businessInfo?.applicationService
      ? [{ id: editingData.businessInfo.applicationService, displayText: editingData.businessInfo.applicationService }]
      : []
  );
  const [selectedApplication, setSelectedApplication] = useState(
    editingData?.businessInfo?.applicationService
      ? [{ id: editingData.businessInfo.applicationService, displayText: editingData.businessInfo.applicationService }]
      : []
  );

  // Auto-filled fields from Business Application
  const [dataClassificationValue, setDataClassificationValue] = useState(
    editingData?.businessInfo?.dataClassification || ''
  );
  const [applicationOwnerValue, setApplicationOwnerValue] = useState(
    editingData?.businessInfo?.applicationOwner || ''
  );

  // Search loading states
  const [isBusinessAppSearching, setIsBusinessAppSearching] = useState(false);
  const [isBusinessAppDetailsLoading, setIsBusinessAppDetailsLoading] = useState(false);
  const businessAppSearchRequestIdRef = useRef(0);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Sector options
  const sectorOptions = [
    { id: "AMESA", displayText: "AMESA" },
    { id: "APAC", displayText: "APAC" },
    { id: "CGF", displayText: "CGF" },
    { id: "EUROPE", displayText: "EUROPE" },
    { id: "NA", displayText: "NA" },
    { id: "LATAM", displayText: "LATAM" },
    { id: "PGCS", displayText: "PGCS" },
    { id: "QFNA", displayText: "QFNA" },
    { id: "SODASTREAM", displayText: "SODASTREAM" },
    { id: "AUTOMATION", displayText: "AUTOMATION" },
  ];

  // Environment options
  const environmentOptions = [
    // { id: "DEV", displayText: "Development" },
    // { id: "QA", displayText: "QA" },
    { id: "NONPROD", displayText: "Non-Production" },
    { id: "PROD", displayText: "Production" },
  ];

  // Data Classification options
  const dataClassificationOptions = [
    { id: "PEPSICO_INTERNAL", displayText: "PEPSICO INTERNAL" },
    { id: "PEPSICO_CONFIDENTIAL", displayText: "PEPSICO CONFIDENTIAL" },
    { id: "PEPSICO_PUBLIC", displayText: "PEPSICO PUBLIC" },
    { id: "PEPSICO_RESTRICTED", displayText: "PEPSICO RESTRICTED" },
  ];

  // Update state when editingData changes (coming back from details page with Change button)
  useEffect(() => {
    if (editingData?.businessInfo) {
      // Update Business Application
      if (editingData.businessInfo.businessApplication) {
        setSelectedBusinessApp({
          id: editingData.businessInfo.businessApplication,
          label: editingData.businessInfo.businessApplication
        });
      }
      // Update Sector
      if (editingData.businessInfo.sector) {
        const sectorOption = sectorOptions.find(opt => opt.displayText === editingData.businessInfo.sector);
        if (sectorOption) {
          setSelectedSector([sectorOption]);
        }
      }
      // Update Environment
      if (editingData.businessInfo.environment) {
        const envOption = environmentOptions.find(opt => opt.displayText === editingData.businessInfo.environment);
        if (envOption) {
          setSelectedEnvironment([envOption]);
        }
      }
      // Update Application Service
      if (editingData.businessInfo.applicationService) {
        const serviceOption = { id: editingData.businessInfo.applicationService, displayText: editingData.businessInfo.applicationService };
        setApplicationServiceOptions([serviceOption]);
        setSelectedApplication([serviceOption]);
      }
      // Update Data Classification
      if (editingData.businessInfo.dataClassification) {
        setDataClassificationValue(editingData.businessInfo.dataClassification);
      }
      // Update Application Owner
      if (editingData.businessInfo.applicationOwner) {
        setApplicationOwnerValue(editingData.businessInfo.applicationOwner);
      }
    }
  }, [editingData]);

  // Helper function to find option by displayText
  const findOptionByDisplayText = (options, displayText) => {
    if (!displayText) return [];
    const found = options.find(opt => opt.displayText === displayText);
    return found ? [found] : [];
  };

  // Get default values from editing data
  const getDefaultSector = () => findOptionByDisplayText(sectorOptions, editingData?.businessInfo?.sector);
  const getDefaultEnvironment = () => findOptionByDisplayText(environmentOptions, editingData?.businessInfo?.environment);
  const getDefaultDataClassification = () => findOptionByDisplayText(dataClassificationOptions, editingData?.businessInfo?.dataClassification);

  // Get default search values from editing data
  const getDefaultBusinessApp = () => {
    if (editingData?.businessInfo?.businessApplication) {
      return { label: editingData.businessInfo.businessApplication };
    }
    return null;
  };
  const getDefaultAppService = () => {
    if (editingData?.businessInfo?.applicationService) {
      return { label: editingData.businessInfo.applicationService };
    }
    return null;
  };
  const getDefaultAppOwner = () => {
    if (editingData?.businessInfo?.applicationOwner) {
      return { label: editingData.businessInfo.applicationOwner };
    }
    return null;
  };

  const { control, handleSubmit, formState: { errors }, setValue, clearErrors, trigger } = useForm({
    mode: "onChange",
    defaultValues: {
      sector: getDefaultSector(),
      environment: getDefaultEnvironment(),
      businessApplication: getDefaultBusinessApp(),
      applicationService: getDefaultAppService(),
      dataClassification: getDefaultDataClassification(),
      applicationOwner: getDefaultAppOwner(),
    },
  });

  // Fetch Business Application details API
  const fetchBusinessAppDetails = async (appName) => {
    if (!appName) return null;
    showLoader();
    try {
      const response = await axios.post(
        `${REACT_APP_API_ENDPOINT}sso/cmdb/application/details`,
        { name: appName.substring(0, 500) }
      );
      return response?.data || null;
    } catch (error) {
      console.error("Error fetching application details:", error);
      if (error?.response?.status !== 404) {
        toast.error(t('sso_onboarding.Error fetching application details'));
      }
      return null;
    } finally {
      hideLoader();
    }
  };

  // Clear all dependent fields when business application changes
  const clearDependentFields = () => {
    setApplicationServiceOptions([]);
    setSelectedApplication([]);
    setValue('applicationService', []);
    setDataClassificationValue('');
    setValue('dataClassification', '');
    setApplicationOwnerValue('');
    setValue('applicationOwner', '');
  };

  // Handle Business Application selection - call details API and auto-fill fields
  const handleBusinessAppSelect = async (val, fieldOnChange) => {
    // Clear old values first when selecting a new business application
    clearDependentFields();
    
    // Handle both single value and array (Search component may return either)
    const selectedVal = Array.isArray(val) ? val[0] : val;
    
    setSelectedBusinessApp(selectedVal);
    setBusinessAppSuggestions([]);
    fieldOnChange(selectedVal);
    
    // Get the application name from the selected value (use label which contains the name)
    const appName = selectedVal?.label || selectedVal?.name || selectedVal?.id || (typeof selectedVal === 'string' ? selectedVal : null);
    
    if (!appName) return;
    
    setIsBusinessAppDetailsLoading(true);
    try {
      // Call the details API to get full application information
      const details = await fetchBusinessAppDetails(appName);

      if (details) {
        // Set Application Service options from API response (applicationServices is an array)
        if (details.applicationServices && Array.isArray(details.applicationServices) && details.applicationServices.length > 0) {
          // Map the array to dropdown options format
          const serviceOptions = details.applicationServices.map((service) => ({
            id: typeof service === 'string' ? service : service.id || service.name,
            displayText: typeof service === 'string' ? service : service.name || service.displayText || service.id
          }));
          setApplicationServiceOptions(serviceOptions);

          // Auto-select if only one value in the list
          if (serviceOptions.length === 1) {
            setSelectedApplication([serviceOptions[0]]);
            setValue('applicationService', [serviceOptions[0]]);
          } else {
            // Let user choose from the list if multiple options
            setSelectedApplication([]);
            setValue('applicationService', []);
          }
        } else {
          // Clear options if applicationServices array is not present
          setApplicationServiceOptions([]);
          setSelectedApplication([]);
          setValue('applicationService', []);
        }

        // Auto-fill Data Classification (read-only field)
        if (details.dataClassification) {
          setDataClassificationValue(details.dataClassification);
          setValue('dataClassification', details.dataClassification);
        } else {
          setDataClassificationValue('PEPSICO INTERNAL');
          setValue('dataClassification', 'PEPSICO INTERNAL');
        }

        // Auto-fill Application Owner (read-only field)
        if (details.applicationOwner || details.applicationOwnerGpid) {
          const ownerLabel = details.applicationOwnerGpid
            ? `${details.applicationOwner || ''}`
            : details.applicationOwner;
          setApplicationOwnerValue(ownerLabel);
          setValue('applicationOwner', ownerLabel);
        }
      } else {
        // Fallback if details API fails - clear options
        setApplicationServiceOptions([]);
        setSelectedApplication([]);
        setValue('applicationService', []);
        setDataClassificationValue('PEPSICO INTERNAL');
        setValue('dataClassification', 'PEPSICO INTERNAL');
      }
    } finally {
      setIsBusinessAppDetailsLoading(false);
    }
  };

  // Debounced search for Business Application (300ms delay per requirements)
  const debouncedBusinessAppSearch = useDebouncedCallback((query) => {
    if (query && query.trim().length >= 2) {
      searchBusinessApplications(query);
    } else {
      setBusinessAppSuggestions([]);
    }
  }, 500);


  // Check if Business Application should be enabled (both sector and environment selected)
  const isBusinessAppEnabled = selectedSector && selectedSector.length > 0 && selectedEnvironment && selectedEnvironment.length > 0;
  const isBusinessAppApiLoading = isBusinessAppSearching || isBusinessAppDetailsLoading;
  
  // Check if Application Service should be enabled (business application selected)
  const isAppServiceEnabled = selectedBusinessApp !== null;

  // Check if all required fields are filled for Continue button
  const isFormComplete = 
    selectedSector && selectedSector.length > 0 &&
    selectedEnvironment && selectedEnvironment.length > 0 &&
    selectedBusinessApp !== null &&
    selectedApplication && selectedApplication.length > 0 &&
    dataClassificationValue &&
    applicationOwnerValue &&
    selectedIntegrationType !== null;

  // Search Business Applications API (Auto-Complete)
  const searchBusinessApplications = async (text) => {
    if (!text || text.trim().length < 2) {
      setBusinessAppSuggestions([]);
      return;
    }
    const requestId = ++businessAppSearchRequestIdRef.current;
    setIsBusinessAppSearching(true);
    showLoader();
    try {
      const response = await axios.post(
        `${REACT_APP_API_ENDPOINT}sso/cmdb/applications`,
        { 
          search: text.substring(0, 200), // max 200 chars
          limit: 20 // default limit
        }
      );
      
      const appList = response?.data?.appList || response?.data || [];
      if (requestId !== businessAppSearchRequestIdRef.current) {
        return;
      }
      if (Array.isArray(appList) && appList.length > 0) {
        // Map the response array to suggestion format expected by pepsico-ds Search
        // API returns { appList: [{ name, id }] }
        const mapped = appList.map((item) => ({
          id: item.id || item.name,
          label: item.name || item.id,
          variant: 'default'
        }));
        setBusinessAppSuggestions(mapped);
      } else {
        setBusinessAppSuggestions([]);
      }
    } catch (error) {
      if (requestId !== businessAppSearchRequestIdRef.current) {
        return;
      }
      if (error?.response?.status === 404) {
        setBusinessAppSuggestions([]);
      } else {
        toast.error(error?.message || t('sso_onboarding.Error searching applications'));
      }
      console.error("Error searching applications:", error);
      setBusinessAppSuggestions([]);
    } finally {
      if (requestId === businessAppSearchRequestIdRef.current) {
        setIsBusinessAppSearching(false);
      }
      hideLoader();
    }
  };


  const onSubmit = (data) => {
    if (!selectedIntegrationType) {
      toast.error(t('sso_onboarding.Please select an SSO type to continue'));
      return;
    }
    
    // Prepare business info data
    const businessInfo = {
      sector: data.sector?.[0]?.displayText || "",
      environment: data.environment?.[0]?.displayText || "",
      businessApplication: selectedBusinessApp?.label || "",
      applicationService: selectedApplication?.[0]?.displayText || "",
      dataClassification: dataClassificationValue || "",
      applicationOwner: applicationOwnerValue || "",
    };

    // Navigate to details page with data
    navigate(detailsRoute, {
      state: {
        businessInfo,
        selectedType: selectedIntegrationType,
        flowType: "onboarding",
      },
    });
  };

  const handleBack = () => {
    navigate('/home');
  };

  return (
    <div className="sso-onboarding-container">
      {/* Header */}
      <div className="sso-details-header">
        <div className="sso-details-header-left">
          <button className="sso-back-button" onClick={handleBack} type="button">
            <Icon icon="arrow_back" size="medium" />
          </button>
          <div className="sso-details-title-section">
            <h1 className="sso-details-title">{pageTitle}</h1>
            <p className="sso-details-subtitle">
              {t('sso_onboarding.Self-service portal that empowers application team to seamlessly initiate onboarding and manage PepsiCo SSO enabled applications.')}
            </p>
          </div>
        </div>
      </div>

      <div className={`sso-details-content ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
        {/* Left Section - Business Information */}
        <div className="sso-details-main">
          <div className="sso-details-card">
            <div className="sso-details-card-header">
              {/* <span className="sso-card-indicator"></span> */}
              <h2 className="sso-details-card-title">{t('sso_onboarding.Business Information')}</h2>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Row 1: Sector & Environment */}
              <div className={`sso-form-row ${isMobile ? 'mobile' : ''}`}>
                {/* Sector Dropdown */}
                <div className="sso-form-field">
                  <Controller
                    name="sector"
                    control={control}
                    rules={{ required: t('sso_onboarding.Sector is required') }}
                    render={({ field }) => (
                      <Dropdown
                        label={t('sso_onboarding.Sector')}
                        required
                        isRequired
                        showLabelIcon={false}
                        isClearable={false}
                        placeholder={t('sso_onboarding.Select Sector')}
                        childList={sectorOptions}
                        selectedValue={field.value}
                        setSelectedValue={(val) => {
                          setSelectedSector(val);
                          field.onChange(val);
                          if (val && val.length > 0) {
                            clearErrors('sector');
                          }
                        }}
                        size="medium"
                        selection="single"
                        isInvalid={!!errors.sector}
                        helperText={errors.sector?.message}
                      />
                    )}
                  />
                </div>

                {/* Environment Dropdown */}
                <div className="sso-form-field">
                  <Controller
                    name="environment"
                    control={control}
                    rules={{ required: t('sso_onboarding.Environment is required') }}
                    render={({ field }) => (
                      <Dropdown
                        label={t('sso_onboarding.Environment')}
                        required
                        isRequired
                        showLabelIcon={false}
                        isClearable={false}
                        placeholder={t('sso_onboarding.Select Environment')}
                        childList={environmentOptions}
                        selectedValue={field.value}
                        setSelectedValue={(val) => {
                          setSelectedEnvironment(val);
                          field.onChange(val);
                          if (val && val.length > 0) {
                            clearErrors('environment');
                          }
                        }}
                        size="medium"
                        selection="single"
                        isInvalid={!!errors.environment}
                        helperText={errors.environment?.message}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Row 2: Business Application & Application Service */}
              <div className={`sso-form-row ${isMobile ? 'mobile' : ''}`}>
                {/* Business Application Search */}
                <div className="sso-form-field">
                  <label className="sso-label">
                    <span className="label-text" style={{fontSize:"0.75rem",fontWeight:"600"}}>{t('sso_onboarding.Business Application')}</span>
                    <span className="required">*{t('sso_onboarding.Required')}</span>
                  </label>
                  <Controller
                    name="businessApplication"
                    control={control}
                    rules={{ required: t('sso_onboarding.Business Application is required') }}
                    render={({ field }) => (
                      <div className="sso-business-app-search-wrapper">
                        <div className="sso-business-app-input-container">
                          <input
                            type="text"
                            className="sso-business-app-input"
                            placeholder={t('sso_onboarding.Search Business Application')}
                            value={selectedBusinessApp?.label || businessAppSearchText || ''}
                            disabled={!isBusinessAppEnabled || isBusinessAppApiLoading}
                            onChange={(e) => {
                              if (isBusinessAppApiLoading) return;
                              const val = e.target.value;
                              setBusinessAppSearchText(val);
                              if (selectedBusinessApp) {
                                setSelectedBusinessApp(null);
                                field.onChange(null);
                              }
                              if (isBusinessAppEnabled && val.length >= 3) {
                                debouncedBusinessAppSearch(val);
                              } else {
                                setBusinessAppSuggestions([]);
                              }
                            }}
                            style={{ opacity: isBusinessAppEnabled && !isBusinessAppApiLoading ? 1 : 0.6 }}
                          />
                          <Icon icon="search" size="small" className="sso-search-icon" />
                          {(selectedBusinessApp?.label || businessAppSearchText) && (
                            <button
                              type="button"
                              className="sso-business-app-clear"
                              disabled={isBusinessAppApiLoading}
                              onClick={() => {
                                if (isBusinessAppApiLoading) return;
                                setSelectedBusinessApp(null);
                                setBusinessAppSearchText('');
                                setBusinessAppSuggestions([]);
                                field.onChange(null);
                                clearDependentFields();
                              }}
                            >
                              <Icon icon="close" size="small" />
                            </button>
                          )}
                        </div>
                        {businessAppSuggestions.length > 0 && (
                          <ul className="sso-business-app-suggestions">
                            {businessAppSuggestions.map((item) => (
                              <li
                                key={item.id}
                                className="sso-business-app-suggestion-item"
                                onClick={async () => {
                                  if (isBusinessAppApiLoading) return;
                                  setBusinessAppSearchText('');
                                  await handleBusinessAppSelect(item, field.onChange);
                                  setBusinessAppSuggestions([]);
                                  clearErrors('businessApplication');
                                }}
                              >
                                {item.label}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  />
                  {errors.businessApplication && (
                    <div className="sso-error-message">
                      <Icon icon="info" size="small" />
                      <span>{errors.businessApplication.message}</span>
                    </div>
                  )}
                </div>

                {/* Application Service Dropdown */}
                <div className="sso-form-field">
                  <Controller
                    name="applicationService"
                    control={control}
                    rules={{ required: t('sso_onboarding.Application Service is required') }}
                    render={({ field }) => (
                      <Dropdown
                        label={t('sso_onboarding.Application Service')}
                        required
                        isRequired
                        disabled={!isAppServiceEnabled}
                        placeholder={t('sso_onboarding.Application Service')}
                        childList={applicationServiceOptions}
                        showLabelIcon={false}
                        selectedValue={selectedApplication}
                        setSelectedValue={(val) => {
                          setSelectedApplication(val);
                          field.onChange(val);
                          if (val && val.length > 0) {
                            clearErrors('applicationService');
                          }
                        }}
                        size="medium"
                        selection="single"
                        isInvalid={!!errors.applicationService}
                        helperText={errors.applicationService?.message}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Row 3: Data Classification & Application Owner */}
              <div className={`sso-form-row ${isMobile ? 'mobile' : ''}`}>
                {/* Data Classification - Read-only TextInput */}
                <div className="sso-form-field">
                  <Controller
                    name="dataClassification"
                    control={control}
                    rules={{ required: t('sso_onboarding.Data Classification is required') }}
                    render={({ field, fieldState }) => (
                      <>
                        <TextInput
                          label={t('sso_onboarding.Data Classification')}
                          required
                          disabled
                          value={dataClassificationValue}
                          placeholderText={t('sso_onboarding.Data Classification')}
                          size="medium"
                          style={{ width: "100%" }}
                        />
                        {fieldState.error && !dataClassificationValue && (
                          <div className="sso-error-message">
                            <Icon icon="info" size="small" />
                            <span>{fieldState.error.message}</span>
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>

                {/* Application Owner - Read-only TextInput */}
                <div className="sso-form-field">
                  <Controller
                    name="applicationOwner"
                    control={control}
                    rules={{ required: t('sso_onboarding.Application Owner is required') }}
                    render={({ field, fieldState }) => (
                      <>
                        <TextInput
                          label={t('sso_onboarding.Application Owner')}
                          required
                          disabled
                          value={applicationOwnerValue}
                          placeholderText={t('sso_onboarding.Application Owner')}
                          size="medium"
                          style={{ width: "100%" }}
                        />
                        {fieldState.error && !applicationOwnerValue && (
                          <div className="sso-error-message">
                            <Icon icon="info" size="small" />
                            <span>{fieldState.error.message}</span>
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
              </div>

              {/* Integration Type Selection */}
              <div className="sso-integration-section">
                <h3 className="sso-integration-title">
                  {t('sso_onboarding.Choose an SSO type to continue')}
                </h3>
                <p className="sso-integration-subtitle">
                  {t('sso_onboarding.Select one of the options below to configure your single sign-on integration.')}
                  {t('sso_onboarding.Each type has different requirements and use cases.')}
                </p>

                <div className={` sso-integration-cards ${isMobile ? 'mobile' : ''}`}>
                  {/* SAML 2.0 Card */}
                  <div 
                    className={`sso-saml-bg sso-integration-card ${selectedIntegrationType === 'saml' ? 'selected' : ''}`}
                    onClick={() => setSelectedIntegrationType('saml')}
                  >
                    <div className="sso-integration-number sso-number-blue">1</div>
                    <h4 className="sso-integration-card-title">{t('sso_onboarding.SAML 2.0')}</h4>
                    <p className="sso-integration-card-desc">
                      {t('sso_onboarding.Traditional enterprise SSO protocol for web applications with robust security features.')}
                    </p>
                  </div>

                  {/* OIDC Card */}
                  <div 
                    className={`sso-oidc-bg sso-integration-card ${selectedIntegrationType === 'oidc' ? 'selected' : ''}`}
                    onClick={() => setSelectedIntegrationType('oidc')}
                  >
                    <div className="sso-integration-number sso-number-yellow">2</div>
                    <h4 className="sso-integration-card-title">{t('sso_onboarding.OIDC - OpenID Connect')}</h4>
                    <p className="sso-integration-card-desc">
                      {t('sso_onboarding.Modern authentication protocol built on OAuth 2.0, ideal for web and mobile apps.')}
                    </p>
                  </div>

                  {/* OIDC M2M Card */}
                  <div 
                    className={`sso-m2m-bg sso-integration-card ${selectedIntegrationType === 'oidc-m2m' ? 'selected' : ''}`}
                    onClick={() => setSelectedIntegrationType('oidc-m2m')}
                  >
                    <div className="sso-integration-number sso-number-green">3</div>
                    <h4 className="sso-integration-card-title">{t('sso_onboarding.OIDC - Machine2Machine(API)')}</h4>
                    <p className="sso-integration-card-desc">
                      {t('sso_onboarding.Secure authentication for server-to-server API communication without user interaction.')}
                    </p>
                  </div>
                </div>

                {/* Continue Button */}
                <div className="sso-continue-section">
                  <Button
                    variant="primary"
                    size="medium"
                    onClick={handleSubmit(onSubmit)}
                    disabled={!isFormComplete}
                  >
                    {t('sso_onboarding.Continue')}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Right Section - Info Cards */}
        <div className="sso-details-sidebar">
          {/* Project Card */}
          <div className="sso-info-card">
            <div className="sso-info-header">
              <Icon icon="fact_check" size="medium" className="sso-info-icon" />
              <h3 className="sso-info-title">{t('sso_onboarding.Project')}</h3>
            </div>
            <div className="sso-info-links">
              <a href="#" className="sso-link-tag" target="_blank" rel="noopener noreferrer">
                OIDC <Icon icon="open_in_new" size="small" />
              </a>
              <a href="#" className="sso-link-tag" target="_blank" rel="noopener noreferrer">
                OIDC-Machine2Machine(API Service) <Icon icon="open_in_new" size="small" />
              </a>
              <a href="#" className="sso-link-tag" target="_blank" rel="noopener noreferrer">
                SAML <Icon icon="open_in_new" size="small" />
              </a>
            </div>
          </div>

          {/* Help & FAQ Card */}
          <div className="sso-info-card">
            <div className="sso-info-header">
              <Icon icon="help_outline" size="medium" className="sso-info-icon" />
              <h3 className="sso-info-title">{t('sso_onboarding.Help & FAQ on SSO Integration')}</h3>
            </div>
            <div className="sso-info-links">
              {/* <a href="#" className="sso-link-button" target="_blank" rel="noopener noreferrer">
                DevX FAQ Site <Icon icon="open_in_new" size="small" />
              </a> */}
              <a href={ASSOCIATE_SSO_FAQ_URL} className="sso-link-button" target="_blank" rel="noopener noreferrer">
                SSO FAQ Site <Icon icon="open_in_new" size="small" />
              </a>
            </div>
          </div>

          {/* Help Guide Card */}
          <div className="sso-info-card">
            <div className="sso-info-header">
              <Icon icon="policy" size="medium" className="sso-info-icon help-guide" />
              <h3 className="sso-info-title"><a href={ASSOCIATE_SSO_FAQ_URL} target="_blank" rel="noopener noreferrer">{t('sso_onboarding.Help Guide')}</a></h3>
            </div>
            <p className="sso-info-text">
              {t('sso_onboarding.Please review the')} <a href={ASSOCIATE_SSO_FAQ_URL} className="sso-text-link" target="_blank" rel="noopener noreferrer">{t('sso_onboarding.Help Guide')}</a> {t('sso_onboarding.for supported integration patterns, required metadata/configuration parameters, and best practices.')}
            </p>
            <div className="sso-info-note">
              <p>
                {t('sso_onboarding.For additional support with SSO integrations, submit a Service Request to ASSOCIATE SSO Operations.')}
              </p>
              <p>
                <strong>{t('sso_onboarding.Note')}:</strong> {t('sso_onboarding.SRs are processed per standard SLAs and cannot be expedited.')}
              </p>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer 
        position="bottom-center"
        autoClose={5000}
        hideProgressBar={true}
        closeOnClick
        pauseOnHover
        draggable
      />
    </div>
  );
};

export default SSOOnboarding;
